// Pillar.jsx
// Full-screen chat interface for a pillar agent.
// Handles session init, message streaming, special message types (suggestions,
// pivot recommendations), and session close with memory write-back.
// All API calls go through src/api.js. No external UI libraries.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sendPillarMessage, closePillarSession } from '../api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateSessionId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function slugToLabel(slug) {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Parse an agent response string and return a structured message object.
 * Detects [PIVOT:slug] tags and surfaces them as a separate card.
 * Detects "Suggestion:" prefix and renders with orange left border.
 */
function parseAgentResponse(text) {
  const pivotMatch = text.match(/\[PIVOT:([a-z-]+)\]/);
  if (pivotMatch) {
    const pivotSlug = pivotMatch[1];
    const cleanText = text.replace(/\[PIVOT:[a-z-]+\]\s*/g, '').trim();
    return { role: 'agent', content: cleanText, type: 'pivot', pivotSlug };
  }

  if (text.trimStart().startsWith('Suggestion:')) {
    const body = text.replace(/^Suggestion:\s*/i, '').trim();
    return { role: 'agent', content: body, type: 'suggestion' };
  }

  return { role: 'agent', content: text, type: 'normal' };
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Pillar() {
  const { pillarId } = useParams();
  const navigate = useNavigate();

  // Stable session ID for the lifetime of this page mount.
  const [sessionId] = useState(generateSessionId);

  // Messages rendered in the thread. Each: { role, content, type, pivotSlug? }
  const [messages, setMessages] = useState([]);

  // Full conversation in Anthropic format — sent with every request.
  // The backend is stateless; this array is the source of truth for history.
  const [conversationHistory, setConversationHistory] = useState([]);

  const [inputValue, setInputValue] = useState('');
  const [isAgentThinking, setIsAgentThinking] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [initError, setInitError] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const pillarLabel = slugToLabel(pillarId);

  // Auto-scroll to newest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isAgentThinking]);

  // Send the session-start signal on mount to get the opening message.
  useEffect(() => {
    async function initSession() {
      setIsAgentThinking(true);
      try {
        const data = await sendPillarMessage(pillarId, '__session_start__', sessionId, []);
        const agentMsg = parseAgentResponse(data.response);
        setMessages([agentMsg]);
        setConversationHistory([
          { role: 'user', content: '__session_start__' },
          { role: 'assistant', content: data.response },
        ]);
      } catch {
        setInitError(true);
      } finally {
        setIsAgentThinking(false);
        inputRef.current?.focus();
      }
    }

    initSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = useCallback(async () => {
    const message = inputValue.trim();
    if (!message || isAgentThinking) return;

    setInputValue('');
    setMessages((prev) => [...prev, { role: 'user', content: message, type: 'normal' }]);

    // Snapshot history before the new user message so we pass prior context only.
    const historySnapshot = conversationHistory;
    setIsAgentThinking(true);

    try {
      const data = await sendPillarMessage(pillarId, message, sessionId, historySnapshot);
      const agentMsg = parseAgentResponse(data.response);
      setMessages((prev) => [...prev, agentMsg]);
      setConversationHistory((prev) => [
        ...prev,
        { role: 'user', content: message },
        { role: 'assistant', content: data.response },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          content: 'Something went wrong. Please try again.',
          type: 'normal',
        },
      ]);
    } finally {
      setIsAgentThinking(false);
      inputRef.current?.focus();
    }
  }, [inputValue, isAgentThinking, conversationHistory, pillarId, sessionId]);

  async function handleEndSession() {
    if (isEnding) return;
    setIsEnding(true);
    try {
      await closePillarSession(pillarId, sessionId, 'memory_update', conversationHistory);
    } catch {
      // Don't block navigation on close failure — memory can be recovered.
    }
    navigate('/dashboard');
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  }

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes blink {
          0%, 80%, 100% { opacity: 0.2; }
          40% { opacity: 1; }
        }
        textarea::placeholder { color: rgba(255,255,255,0.25); }
        textarea:focus { outline: none; border-color: rgba(255,107,53,0.5); }
      `}</style>

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div style={styles.topBar}>
        <div style={styles.topLeft}>
          <button
            style={styles.backBtn}
            onClick={() => navigate('/dashboard')}
            disabled={isEnding}
          >
            ← Back to Dashboard
          </button>
          <span style={styles.pillarLabel}>{pillarLabel}</span>
        </div>
        <button
          style={{ ...styles.endBtn, opacity: isEnding ? 0.6 : 1 }}
          onClick={handleEndSession}
          disabled={isEnding}
        >
          {isEnding ? 'Saving…' : 'End Session'}
        </button>
      </div>

      {/* ── Message thread ───────────────────────────────────────────────── */}
      <div style={styles.thread}>
        {initError && (
          <div style={styles.errorNotice}>
            Could not connect to the agent. Please refresh the page and try again.
          </div>
        )}

        {messages.map((msg, index) => (
          <MessageBubble key={index} message={msg} navigate={navigate} />
        ))}

        {isAgentThinking && <TypingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Input bar ────────────────────────────────────────────────────── */}
      <div style={styles.inputBar}>
        <textarea
          ref={inputRef}
          style={styles.inputField}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message… (Enter to send, Shift+Enter for new line)"
          rows={1}
          disabled={isAgentThinking || isEnding}
        />
        <button
          style={{
            ...styles.sendBtn,
            opacity: isAgentThinking || !inputValue.trim() ? 0.4 : 1,
            cursor: isAgentThinking || !inputValue.trim() ? 'not-allowed' : 'pointer',
          }}
          onClick={handleSend}
          disabled={isAgentThinking || !inputValue.trim()}
        >
          Send
        </button>
      </div>
    </div>
  );
}

// ─── Message bubble ───────────────────────────────────────────────────────────

function MessageBubble({ message, navigate }) {
  if (message.role === 'user') {
    return (
      <div style={styles.userRow}>
        <div style={styles.userBubble}>
          <MessageText text={message.content} />
        </div>
      </div>
    );
  }

  if (message.type === 'suggestion') {
    return (
      <div style={styles.agentRow}>
        <Avatar />
        <div style={styles.suggestionCard}>
          <span style={styles.suggestionLabel}>Suggestion</span>
          <MessageText text={message.content} />
        </div>
      </div>
    );
  }

  if (message.type === 'pivot') {
    return (
      <div style={styles.agentRow}>
        <Avatar />
        <div style={styles.agentContent}>
          <MessageText text={message.content} />
          <PivotCard slug={message.pivotSlug} navigate={navigate} />
        </div>
      </div>
    );
  }

  // Normal agent message
  return (
    <div style={styles.agentRow}>
      <Avatar />
      <div style={styles.agentContent}>
        <MessageText text={message.content} />
      </div>
    </div>
  );
}

// Renders text with paragraph breaks preserved.
function MessageText({ text }) {
  const paragraphs = text.split(/\n\n+/);
  return (
    <div>
      {paragraphs.map((para, index) => (
        <p key={index} style={{ ...styles.messageText, marginTop: index > 0 ? '10px' : 0 }}>
          {para}
        </p>
      ))}
    </div>
  );
}

function PivotCard({ slug, navigate }) {
  const label = slugToLabel(slug);
  return (
    <div style={styles.pivotCard}>
      <span style={styles.pivotArrow}>→</span>
      <div style={styles.pivotCardBody}>
        <p style={styles.pivotCardLabel}>Suggested next step</p>
        <p style={styles.pivotCardPillar}>{label}</p>
      </div>
      <button
        style={styles.pivotGoBtn}
        onClick={() => navigate(`/pillar/${slug}`)}
      >
        Go there
      </button>
    </div>
  );
}

function Avatar() {
  return <div style={styles.avatar}>RF</div>;
}

function TypingIndicator() {
  return (
    <div style={styles.agentRow}>
      <Avatar />
      <div style={styles.typingDots}>
        <span style={{ ...styles.dot, animationDelay: '0s' }} />
        <span style={{ ...styles.dot, animationDelay: '0.16s' }} />
        <span style={{ ...styles.dot, animationDelay: '0.32s' }} />
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const ORANGE = '#FF6B35';
const NAVY = '#1A1A2E';
const CARD = '#16213E';
const BORDER = 'rgba(255,255,255,0.06)';
const TEXT = 'rgba(255,255,255,0.88)';
const MUTED = 'rgba(255,255,255,0.35)';

const styles = {
  // ── Layout ────────────────────────────────────────────────────────────────
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: NAVY,
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    color: TEXT,
    overflow: 'hidden',
  },

  // ── Top bar ───────────────────────────────────────────────────────────────
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    height: '60px',
    borderBottom: `1px solid ${BORDER}`,
    backgroundColor: CARD,
    flexShrink: 0,
  },
  topLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: MUTED,
    fontSize: '13px',
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'inherit',
    transition: 'color 0.12s',
  },
  pillarLabel: {
    fontSize: '15px',
    fontWeight: '600',
    color: TEXT,
    letterSpacing: '-0.2px',
  },
  endBtn: {
    padding: '8px 18px',
    backgroundColor: 'transparent',
    border: `1.5px solid rgba(255,107,53,0.4)`,
    borderRadius: '8px',
    color: ORANGE,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity 0.12s',
  },

  // ── Message thread ────────────────────────────────────────────────────────
  thread: {
    flex: 1,
    overflowY: 'auto',
    padding: '32px 24px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    maxWidth: '760px',
    width: '100%',
    alignSelf: 'center',
    boxSizing: 'border-box',
  },

  // ── Agent message ─────────────────────────────────────────────────────────
  agentRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: ORANGE,
    color: '#fff',
    fontSize: '11px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '2px',
    letterSpacing: '0.5px',
  },
  agentContent: {
    flex: 1,
    minWidth: 0,
  },
  messageText: {
    margin: 0,
    fontSize: '15px',
    lineHeight: '1.65',
    color: TEXT,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },

  // ── User message ──────────────────────────────────────────────────────────
  userRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  userBubble: {
    backgroundColor: 'rgba(255,107,53,0.15)',
    border: `1px solid rgba(255,107,53,0.25)`,
    borderRadius: '16px 16px 4px 16px',
    padding: '12px 16px',
    maxWidth: '75%',
  },

  // ── Suggestion card ───────────────────────────────────────────────────────
  suggestionCard: {
    flex: 1,
    borderLeft: `3px solid ${ORANGE}`,
    paddingLeft: '14px',
    paddingTop: '2px',
  },
  suggestionLabel: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '700',
    color: ORANGE,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '6px',
  },

  // ── Pivot card ────────────────────────────────────────────────────────────
  pivotCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '14px',
    padding: '14px 16px',
    backgroundColor: 'rgba(255,107,53,0.08)',
    border: `1px solid rgba(255,107,53,0.25)`,
    borderRadius: '10px',
  },
  pivotArrow: {
    fontSize: '18px',
    color: ORANGE,
    flexShrink: 0,
  },
  pivotCardBody: {
    flex: 1,
  },
  pivotCardLabel: {
    margin: '0 0 2px',
    fontSize: '11px',
    fontWeight: '600',
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  },
  pivotCardPillar: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '600',
    color: TEXT,
  },
  pivotGoBtn: {
    padding: '8px 14px',
    backgroundColor: ORANGE,
    color: '#fff',
    border: 'none',
    borderRadius: '7px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
    flexShrink: 0,
  },

  // ── Typing indicator ──────────────────────────────────────────────────────
  typingDots: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    padding: '10px 0',
  },
  dot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: MUTED,
    animation: 'blink 1.2s ease-in-out infinite',
    display: 'inline-block',
  },

  // ── Error notice ──────────────────────────────────────────────────────────
  errorNotice: {
    padding: '14px 16px',
    backgroundColor: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: '10px',
    color: '#FCA5A5',
    fontSize: '14px',
    lineHeight: '1.5',
  },

  // ── Input bar ─────────────────────────────────────────────────────────────
  inputBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '10px',
    padding: '16px 24px',
    borderTop: `1px solid ${BORDER}`,
    backgroundColor: CARD,
    flexShrink: 0,
    maxWidth: '760px',
    width: '100%',
    alignSelf: 'center',
    boxSizing: 'border-box',
  },
  inputField: {
    flex: 1,
    padding: '12px 14px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    fontSize: '15px',
    color: '#fff',
    fontFamily: 'inherit',
    resize: 'none',
    lineHeight: '1.5',
    maxHeight: '160px',
    overflowY: 'auto',
    boxSizing: 'border-box',
    transition: 'border-color 0.12s',
  },
  sendBtn: {
    padding: '12px 20px',
    backgroundColor: ORANGE,
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'inherit',
    flexShrink: 0,
    transition: 'opacity 0.12s',
    alignSelf: 'flex-end',
  },
};
